import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FlatpickrDefaultsInterface } from 'angularx-flatpickr';
import { NotifierService } from 'angular-notifier';
import { Subject, takeUntil } from 'rxjs';
import {
    CleanupDuplicatesResponse,
    DetectDuplicatesResponse,
    DuplicateModalStatus,
    DuplicateModalStep,
    DuplicateTransactionGroup,
    DuplicateTransactionsConfig,
    DuplicateTransactionsDateRange
} from './duplicate-transactions-modal.model';

/**
 * Reusable Detect & Resolve Duplicate Transactions Modal.
 *
 * Flow: Range (step 1) -> Review (step 2) -> Confirm (step 3).
 * API calls are injected via DuplicateTransactionsConfig so the modal stays
 * feature-agnostic.
 */
@Component({
    selector: 'app-duplicate-transactions-modal',
    templateUrl: './duplicate-transactions-modal.component.html',
    styleUrls: ['./duplicate-transactions-modal.component.scss'],
    standalone: false
})
export class DuplicateTransactionsModalComponent implements OnInit, OnDestroy {

    @Input() config!: DuplicateTransactionsConfig;

    private _unsubscribeAll: Subject<void> = new Subject<void>();

    rangeForm!: UntypedFormGroup;
    confirmForm!: UntypedFormGroup;

    step: DuplicateModalStep = 1;
    status: DuplicateModalStatus = 'idle';

    detectResponse: DetectDuplicatesResponse | null = null;
    resolveResponse: CleanupDuplicatesResponse | null = null;
    expandedGroupIndex: number | null = 0;

    /** Flatpickr options for the "From Date" input — cannot exceed today */
    flatpickrFromOptions: FlatpickrDefaultsInterface = {
        maxDate: new Date(),
        dateFormat: 'Y-m-d',
        allowInput: false
    };

    /** Flatpickr options for the "To Date" input — cannot exceed today */
    flatpickrToOptions: FlatpickrDefaultsInterface = {
        maxDate: new Date(),
        dateFormat: 'Y-m-d',
        allowInput: false
    };

    constructor(
        public activeModal: NgbActiveModal,
        private _formBuilder: FormBuilder,
        private _notifierService: NotifierService
    ) { }

    ngOnInit(): void {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        this.rangeForm = this._formBuilder.group({
            fromDate: [this.toDateInput(yesterday), [Validators.required]],
            toDate: [this.toDateInput(today), [Validators.required]]
        });

        this.confirmForm = this._formBuilder.group({
            confirmationInput: [null, [Validators.required]]
        });
    }

    get expectedConfirmationText(): string {
        return this.config?.confirmationText
            || `I Want To Resolve Duplicates For ${this.config?.confirmationIdentifier || ''}`;
    }

    get isConfirmationValid(): boolean {
        return this.confirmForm?.get('confirmationInput')?.value === this.expectedConfirmationText;
    }

    get showConfirmationError(): boolean {
        const input = this.confirmForm?.get('confirmationInput');
        return !!(input?.touched && input?.value && input.value !== this.expectedConfirmationText);
    }

    get rangeError(): string | null {
        if (this.rangeForm.invalid) return null;
        const { fromDate, toDate } = this.rangeForm.value;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        if (from > to) return 'From Date must be on or before To Date';
        return null;
    }

    get canSubmitRange(): boolean {
        return this.rangeForm.valid && !this.rangeError && this.status !== 'scanning';
    }

    /** Step 1 -> Detect API */
    submitDetect(): void {
        if (!this.canSubmitRange) return;

        const range: DuplicateTransactionsDateRange = {
            fromDate: this.rangeForm.value.fromDate,
            toDate: this.rangeForm.value.toDate
        };

        this.status = 'scanning';
        this.config.onDetect(range)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.detectResponse = response;
                    const hasGroups = (response?.duplicateGroupsFound ?? 0) > 0
                        && (response?.duplicateGroups?.length ?? 0) > 0;
                    this.status = hasGroups ? 'reviewing' : 'empty';
                    this.step = 2;
                    this.expandedGroupIndex = hasGroups ? 0 : null;
                },
                error: () => {
                    this.status = 'error';
                }
            });
    }

    toggleGroup(index: number): void {
        this.expandedGroupIndex = this.expandedGroupIndex === index ? null : index;
    }

    /** Step 2 -> Step 3 */
    goToConfirm(): void {
        if (this.status !== 'reviewing') return;
        this.step = 3;
    }

    /** Step 3 -> Step 2 */
    backToReview(): void {
        if (this.status === 'submitting' || this.status === 'done') return;
        this.step = 2;
    }

    /** Step 1 <-> Step 2 back navigation */
    backToRange(): void {
        if (this.status === 'submitting' || this.status === 'done') return;
        this.step = 1;
        this.status = 'idle';
        this.detectResponse = null;
    }

    /** Step 3 -> Cleanup API */
    submitResolve(): void {
        if (!this.isConfirmationValid || this.status === 'submitting') return;

        const range: DuplicateTransactionsDateRange = {
            fromDate: this.rangeForm.value.fromDate,
            toDate: this.rangeForm.value.toDate
        };

        this.status = 'submitting';
        this.config.onResolve(range)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.resolveResponse = response;
                    this.status = 'done';
                    this._notifierService.notify('success', this.config.successMessage);
                },
                error: () => {
                    this.status = 'reviewing';
                }
            });
    }

    onCancel(): void {
        if (this.status === 'submitting') return;
        if (this.status === 'done') {
            this.activeModal.close('resolved');
            return;
        }
        this.activeModal.dismiss('cancel');
    }

    trackGroup(_: number, group: DuplicateTransactionGroup): string {
        return group.keepId || group.transactionId;
    }

    trackTx(_: number, tx: { id: string }): string {
        return tx.id;
    }

    private toDateInput(date: Date): string {
        const y = date.getFullYear();
        const m = ('0' + (date.getMonth() + 1)).slice(-2);
        const d = ('0' + date.getDate()).slice(-2);
        return `${y}-${m}-${d}`;
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
